import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LangProvider, useT } from "./lib/i18n";
import { AuthProvider, useAuth } from "./lib/auth";
import { api } from "./lib/api";
import { Header, Footer, BottomNav } from "./components/Chrome";
import { InstallPrompt } from "./components/InstallPrompt";
import { RequireRole } from "./components/Guards";
import Home from "./pages/Home";
import { JobsPage, JobDetail } from "./pages/Jobs";
import { Login, ChooseRole, ForgotPassword, ResetPassword } from "./pages/Auth";
import CandidateWizard from "./pages/CandidateWizard";
import { MyApplications, MySaved, MyProfile, MyCV } from "./pages/Candidate";
import EmployerLanding from "./pages/EmployerLanding";
import { EmployerDashboard, EmployerSetup, MyJobs } from "./pages/Employer";
import PostJob from "./pages/PostJob";
import { EmployerApplications, CandidateSearch, CandidateDetail } from "./pages/EmployerApps";
import Subscription from "./pages/Subscription";
import Admin from "./pages/Admin";

function SetupBanner() {
  const { t } = useT();
  const [h, setH] = useState(null);
  useEffect(() => {
    api.get("/health").then((r) => setH(r.data)).catch(() => setH({ database_ready: false }));
  }, []);
  if (!h || h.database_ready) return null;
  return <div className="bg-amber-100 px-4 py-3 text-center text-sm font-semibold text-amber-900" data-testid="setup-banner">{t("setup_needed")}</div>;
}

function NotFound() {
  const { t } = useT();
  return (
    <div className="narrow py-20 text-center">
      <h1 className="text-3xl font-bold text-[color:var(--navy)]">{t("not_found")}</h1>
      <Link to="/" className="btn-orange mt-8" data-testid="notfound-home-btn">{t("go_home")}</Link>
    </div>
  );
}

const C = (el) => <RequireRole role="candidate">{el}</RequireRole>;
const E = (el) => <RequireRole role="employer">{el}</RequireRole>;

function Shell() {
  const loc = useLocation();
  const { user } = useAuth();
  const isCand = user?.role === "candidate";
  const appLike = /^\/(me|start|employer|admin|choose-role)/.test(loc.pathname) || loc.pathname.startsWith("/employer/");
  return (
    <div className={`flex min-h-screen flex-col ${isCand ? "pb-20 md:pb-0" : ""}`}>
      <SetupBanner />
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:id" element={<JobDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/choose-role" element={<ChooseRole />} />
          <Route path="/start" element={<CandidateWizard />} />
          <Route path="/me/applications" element={C(<MyApplications />)} />
          <Route path="/me/saved" element={C(<MySaved />)} />
          <Route path="/me/profile" element={C(<MyProfile />)} />
          <Route path="/me/cv" element={C(<MyCV />)} />
          <Route path="/employers" element={<EmployerLanding />} />
          <Route path="/employer" element={E(<EmployerDashboard />)} />
          <Route path="/employer/setup" element={E(<EmployerSetup />)} />
          <Route path="/employer/jobs" element={E(<MyJobs />)} />
          <Route path="/employer/jobs/new" element={E(<PostJob />)} />
          <Route path="/employer/jobs/:id/edit" element={E(<PostJob />)} />
          <Route path="/employer/applications" element={E(<EmployerApplications />)} />
          <Route path="/employer/candidates" element={E(<CandidateSearch />)} />
          <Route path="/employer/candidates/:id" element={E(<CandidateDetail />)} />
          <Route path="/employer/subscription" element={E(<Subscription />)} />
          <Route path="/admin" element={<RequireRole role="admin"><Admin /></RequireRole>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!appLike && <Footer />}
      {isCand && <BottomNav />}
      <InstallPrompt path={loc.pathname} />
    </div>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
